import React from 'react';
import useGameStore from '../engine/store';
import { formatPrice } from '../engine/scenarios';

export default function ResultsScreen() {
  const {
    lastScore, scenario, finalPrice, playerRole, opponentName, personality,
    round, timeLimit, timeRemaining, playerBluffCount, bluffSuccesses, pressuresUsed,
    dealReached, startNegotiation, goToMenu, winStreak,
  } = useGameStore();

  if (!lastScore || !scenario) return null;

  const isBuyer = playerRole === 'buyer';
  const target = scenario.playerTarget;
  const list = scenario.listPrice;

  // How much value captured
  let valueCaptured;
  if (dealReached) {
    const range = Math.abs(target - list);
    if (isBuyer) {
      valueCaptured = range > 0 ? ((list - finalPrice) / range * 100) : 0;
    } else {
      valueCaptured = range > 0 ? ((finalPrice - list) / range * 100) : 0;
    }
  }

  return (
    <div className="min-h-screen bg-dark-900 bg-grid flex items-center justify-center p-4">
      <div className="max-w-lg w-full animate-slide-up">
        {/* Header */}
        <div className="text-center mb-8">
          {dealReached ? (
            <>
              <div className="text-7xl mb-3">🤝</div>
              <h1 className="text-3xl font-black text-white mb-1">Deal Closed!</h1>
              <div className="text-2xl font-black font-mono text-neon-green">
                {formatPrice(finalPrice, scenario)}
              </div>
            </>
          ) : (
            <>
              <div className="text-7xl mb-3">💨</div>
              <h1 className="text-3xl font-black text-neon-red mb-1">No Deal</h1>
              <p className="text-gray-400">The negotiation fell through.</p>
            </>
          )}
        </div>

        {/* Grade */}
        <div className="text-center mb-6">
          <div
            className="inline-block text-7xl font-black font-mono px-8 py-2 rounded-2xl border-2"
            style={{
              color: lastScore.gradeColor,
              borderColor: `${lastScore.gradeColor}40`,
              background: `${lastScore.gradeColor}10`,
              textShadow: `0 0 30px ${lastScore.gradeColor}40`,
            }}
          >
            {lastScore.grade}
          </div>
          <p className="text-gray-400 text-sm mt-3">{lastScore.summary}</p>

          {lastScore.streakBonus?.label && (
            <div className="mt-2 text-sm font-bold text-neon-yellow animate-pulse-slow">
              {lastScore.streakBonus.label} ({lastScore.streakBonus.multiplier}x)
            </div>
          )}
        </div>

        {/* Score breakdown */}
        <div className="glass-panel p-5 mb-6">
          <div className="text-center mb-4">
            <div className="text-xs text-gray-500 uppercase tracking-wider">Total Score</div>
            <div className="text-4xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-neon-green via-neon-blue to-neon-purple">
              {lastScore.total}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Deal Value</div>
              <div className="text-lg font-bold font-mono text-neon-green">{lastScore.dealValue}</div>
              <div className="text-[10px] text-gray-600">/ 500</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Efficiency</div>
              <div className="text-lg font-bold font-mono text-neon-blue">{lastScore.efficiency}</div>
              <div className="text-[10px] text-gray-600">/ 200</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Technique</div>
              <div className="text-lg font-bold font-mono text-neon-purple">{lastScore.technique}</div>
              <div className="text-[10px] text-gray-600">/ 300</div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="glass-panel p-4 mb-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Scenario</span>
              <span className="text-white">{scenario.emoji} {scenario.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Opponent</span>
              <span className="text-white">{opponentName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Rounds</span>
              <span className="font-mono text-white">{round}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Time Used</span>
              <span className="font-mono text-white">{timeLimit - timeRemaining}s / {timeLimit}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Bluffs</span>
              <span className="font-mono text-neon-orange">
                {playerBluffCount} ({bluffSuccesses} hit)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Pressure</span>
              <span className="font-mono text-neon-red">{pressuresUsed}x</span>
            </div>
            {dealReached && valueCaptured !== undefined && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-500">Your Target</span>
                  <span className="font-mono text-neon-green">{formatPrice(target, scenario)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Value Captured</span>
                  <span className="font-mono text-neon-yellow">{valueCaptured.toFixed(0)}%</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Streak */}
        {winStreak > 1 && (
          <div className="glass-panel p-3 text-center mb-6">
            <span className="text-neon-yellow font-bold">🔥 Win Streak: {winStreak}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => startNegotiation()} className="btn-primary text-lg px-10 py-4">
            🤝 Next Deal
          </button>
          <button onClick={goToMenu} className="btn-secondary px-8 py-4">
            🏠 Menu
          </button>
        </div>
      </div>
    </div>
  );
}
